import { Component, Input, OnDestroy, ViewChild, ElementRef, AfterViewChecked } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subject, takeUntil, interval, lastValueFrom, delay } from 'rxjs';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { NgxDropzoneModule } from 'ngx-dropzone';
import { ButtonComponent } from 'wecheer-common';
import { MultiLabelsComponent } from 'src/app/common/components/multi-labels/multi-labels.component';
import { inject } from '@angular/core';
import { CampaignsCampaignAdminApiClient, MigrateUsersRequest, MigrationProgressResponse } from 'src/app/api/api-campaigns.service.generated';
import { GUID } from 'src/app/common/utils/GuidUtils';
import { S3Upload } from 'src/app/common/utils/S3Upload';

export interface MigrationProgress {
  total: number;
  processed: number;
  successful: number;
  failed: number;
  skipped: number;
  percentage: number;
}

export interface MigrationLog {
  timestamp: Date;
  level: 'info' | 'success' | 'warning' | 'error';
  message: string;
}

export interface MigrationResult {
  migrationId?: string;
  id?: string;
  MigrationId?: string;
  ID?: string;
  migration_id?: string;
  status?: string;
  message?: string;
  success?: boolean;
  errorReportS3Url?: string;
  ErrorReportS3Url?: string;
  error_report_s3_url?: string;
  data?: any;
  result?: any;
  value?: any;
  toJSON?(): any;
  init?(data: any): void;
}

export interface ActionResultWrapper<T = any> {
  toJSON(): T;
  init(data: T): void;
  [key: string]: any;
}

export interface MigrationOptions {
  revokeOldCampaigns: boolean;
  enableDebitPoints: boolean;
  enableCreditPoints: boolean;
  enableAddCard: boolean;
  enableAddTags: boolean;
  customTags: { [key: string]: string }[];
}

// Temporary interface until API service is regenerated
export interface IMigrateUsersRequest {
  migrationId?: string;
  csvS3Url?: string;
  advertiserId?: string;
  isRevoke?: boolean;
  isCreditPoint?: boolean;
  isDebitPoint?: boolean;
  isAddCard?: boolean;
  isAddTags?: boolean;
  customTags?: { [key: string]: string }[];
}

@Component({
  selector: 'app-migrate-users-tab',
  templateUrl: './migrate-users-tab.component.html',
  styleUrls: ['./migrate-users-tab.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatSlideToggleModule,
    MatProgressBarModule,
    MatTooltipModule,
    MatInputModule,
    MatFormFieldModule,
    NgxDropzoneModule,
    ButtonComponent,
    MultiLabelsComponent
  ]
})
export class MigrateUsersTabComponent implements OnDestroy, AfterViewChecked {
  @Input() advertiserId: string;
  @ViewChild('logContainer', { static: false }) logContainer: ElementRef<HTMLDivElement>;

  private destroy$ = new Subject<void>();
  private fb = inject(FormBuilder);
  private snackBar = inject(MatSnackBar);
  private campaignClient = inject(CampaignsCampaignAdminApiClient);

  migrationForm: FormGroup;
  selectedFiles: File[] = [];
  uploadedFile: File | null = null;
  isMigrationRunning = false;
  migrationCompleted = false;
  migrationEverStarted = false;

  migrationProgress: MigrationProgress = {
    total: 0,
    processed: 0,
    successful: 0,
    failed: 0,
    skipped: 0,
    percentage: 0
  };

  // Computed property for success percentage
  get successPercentage(): number {
    if (this.migrationProgress.total === 0) {
      return 0;
    }
    return Math.round((this.migrationProgress.successful / this.migrationProgress.total) * 100);
  }

  migrationLogs: MigrationLog[] = [];
  private shouldScrollToBottom = false;
  private monitoringSubscription: any = null;
  errorReportS3Url: string | null = null;

  constructor() {
    this.initializeForm();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngAfterViewChecked(): void {
    if (this.shouldScrollToBottom) {
      this.scrollToBottom();
      this.shouldScrollToBottom = false;
    }
  }

  private scrollToBottom(): void {
    if (this.logContainer) {
      try {
        this.logContainer.nativeElement.scrollTop = this.logContainer.nativeElement.scrollHeight;
      } catch (err) {
        console.error('Error when try scroll to bottom! ', err);
      }
    }
  }

  private initializeForm(): void {
    this.migrationForm = this.fb.group({
      revokeOldCampaigns: [false],
      enableDebitPoints: [false],
      enableCreditPoints: [false],
      enableAddCard: [true],
      enableAddTags: [false],
      customTags: [[]]
    });

    this.migrationForm.get('customTags')?.valueChanges.subscribe(tags => {
      if (tags && Array.isArray(tags)) {
        const invalidTags = tags.filter((tag: string) => !this.isValidTagFormat(tag));
        if (invalidTags.length > 0) {
          this.addLog('warning', `Invalid tag format detected. Please use "key:value" format. Invalid tags: ${invalidTags.join(', ')}`);
        }
      }
    });
  }

  private isValidTagFormat(tag: string): boolean {
    return typeof tag === 'string' && tag.includes(':') && tag.split(':').length >= 2;
  }

  private convertTagsToKeyValuePairs(tags: string[]): { [key: string]: string }[] {
    return tags
      .filter(tag => this.isValidTagFormat(tag))
      .map(tag => {
        const [key, ...valueParts] = tag.split(':');
        const value = valueParts.join(':');
        return { [key.trim()]: value.trim() };
      });
  }

  onFileSelect(event: any): void {
    const files = event.addedFiles;

    if (files && files.length > 0) {
      const file = files[0];

      if (!file.name.toLowerCase().endsWith('.csv')) {
        this.snackBar.open('Please select a CSV file', 'Close', { duration: 3000 });
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        this.snackBar.open('File size must be less than 5MB', 'Close', { duration: 3000 });
        return;
      }

      this.validateCsvFormat(file);
    }
  }

  private validateCsvFormat(file: File): void {
    const reader = new FileReader();
    reader.onload = (e: any) => {
      try {
        const csvContent = e.target.result as string;
        const validation = this.validateCsvContent(csvContent);

        if (validation.isValid) {
          this.selectedFiles = [file];
          this.addLog('success', `File validated successfully: ${file.name} (${validation.campaignColumns} campaign columns found)`);
        } else {
          this.snackBar.open(`CSV validation failed: ${validation.error}`, 'Close', { duration: 5000 });
          this.addLog('error', `CSV validation failed: ${validation.error}`);
        }
      } catch (error) {
        this.snackBar.open('Error reading CSV file', 'Close', { duration: 3000 });
        this.addLog('error', `Error reading CSV file: ${error}`);
      }
    };

    reader.onerror = () => {
      this.snackBar.open('Error reading file', 'Close', { duration: 3000 });
      this.addLog('error', 'Error reading file');
    };

    reader.readAsText(file);
  }

  private validateCsvContent(csvContent: string): { isValid: boolean; error?: string; campaignColumns?: number } {
    const lines = csvContent.trim().split('\n');

    if (lines.length < 1) {
      return { isValid: false, error: 'CSV file must contain at least a header row' };
    }

    if (lines.length > 500) {
      return { isValid: false, error: `CSV file contains ${lines.length} rows but maximum allowed is 500 rows (including header)` };
    }

    // Get header row and split by tab or comma
    const headerRow = lines[0].trim();
    const columns = headerRow.split(/[\t,]/).map(col => col.trim());

    const requiredColumns = ['user_id', 'point_credit', 'point_debit'];

    const missingColumns = requiredColumns.filter(col => !columns.includes(col));
    if (missingColumns.length > 0) {
      return {
        isValid: false,
        error: `Missing required columns: ${missingColumns.join(', ')}. Required columns are: ${requiredColumns.join(', ')}, revoked_campaign_id, campaign_id_1, campaign_id_2, ...`
      };
    }

    const campaignColumns = columns.filter(col => /^campaign_id_\d+$/.test(col));
    if (campaignColumns.length === 0) {
      return {
        isValid: false,
        error: 'At least one campaign_id column is required (campaign_id_1, campaign_id_2, etc.)'
      };
    }

    const campaignNumbers = campaignColumns
      .map(col => parseInt(col.replace('campaign_id_', '')))
      .sort((a, b) => a - b);

    for (let i = 0; i < campaignNumbers.length; i++) {
      if (campaignNumbers[i] !== i + 1) {
        return {
          isValid: false,
          error: `Campaign ID columns must be sequential starting from campaign_id_1. Found: ${campaignColumns.join(', ')}`
        };
      }
    }

    return {
      isValid: true,
      campaignColumns: campaignColumns.length
    };
  }

  onFileRemove(file: File): void {
    const index = this.selectedFiles.indexOf(file);
    if (index > -1) {
      this.selectedFiles.splice(index, 1);
      this.addLog('info', `File removed: ${file.name}`);
    }
  }

  downloadTemplate(): void {
    const csvContent = [
      'user_id,point_credit,point_debit,revoked_campaign_id,campaign_id_1,campaign_id_2,campaign_id_3',
      'user123,100,50,revoked_campaign_001,campaign_001,campaign_002,campaign_003',
      'user456,200,75,revoked_campaign_002,campaign_001,,campaign_004',
      'user789,150,25,revoked_campaign_003,campaign_002,campaign_003'
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'migration_users_template.csv';
    link.click();
    window.URL.revokeObjectURL(url);

    this.addLog('info', 'CSV migration template downloaded');
    this.addLog('info', 'Required fields: user_id, point_credit, point_debit, campaign_id_1+');
    this.addLog('info', 'Note: When using custom tags, use key:value format (e.g., organizationId:123, staffRole:true). Default currency CHC will be used for all transactions.');
  }

  canStartMigration(): boolean {
    return this.selectedFiles.length > 0 && !this.isMigrationRunning;
  }

  async startMigration(): Promise<void> {
    if (!this.canStartMigration()) {
      return;
    }

    this.isMigrationRunning = true;
    this.migrationCompleted = false;
    this.migrationEverStarted = true;
    this.resetProgress();

    const formData = this.migrationForm.value;
    this.addLog('info', 'Starting migration process...');

    const customTagsAsKeyValue = formData.enableAddTags && formData.customTags?.length
      ? this.convertTagsToKeyValuePairs(formData.customTags)
      : [];

    const tagsDisplay = customTagsAsKeyValue.length
      ? customTagsAsKeyValue.map(pair => {
          const key = Object.keys(pair)[0];
          return `${key}:${pair[key]}`;
        }).join(', ')
      : '';

    this.addLog('info', `Options: Revoke old campaigns: ${formData.revokeOldCampaigns}, Debit points: ${formData.enableDebitPoints}, Credit points: ${formData.enableCreditPoints}, Add card: ${formData.enableAddCard}, Add tags: ${formData.enableAddTags}${tagsDisplay ? ` (${tagsDisplay})` : ''}`);

    try {
      // Upload CSV file to S3
      this.addLog('info', 'Uploading CSV file to S3...');
      const file = this.selectedFiles[0];
      const uploadKey = await this.uploadFileToS3(file);

      if (!uploadKey) {
        this.addLog('error', 'Failed to upload CSV file to S3');
        this.isMigrationRunning = false;
        return;
      }

      this.addLog('success', 'CSV file uploaded successfully to S3');
      this.addLog('info', 'Starting user migration from CSV...');

      const processedFormData = {
        ...formData,
        customTags: formData.enableAddTags && formData.customTags?.length
          ? this.convertTagsToKeyValuePairs(formData.customTags)
          : []
      };

      await this.startMigrationProcess(uploadKey, file.name, processedFormData);

    } catch (error) {
      this.addLog('error', `Migration failed: ${error}`);
      this.snackBar.open('Migration failed. Please check the logs for details.', 'Close', { duration: 5000 });
      this.isMigrationRunning = false;
    }
  }

  private async uploadFileToS3(file: File): Promise<string | null> {
    try {
      const uploadKey = `migration-campaigns/${GUID.newGuid().replace(/-/g, '')}.${file.name.split('.').pop()}`;

      const uploadUrl = await lastValueFrom(this.campaignClient.getUploadUrl(uploadKey));

      // Upload file to S3
      const uploadSuccess = await lastValueFrom(S3Upload.upload(uploadUrl as string, file));

      if (uploadSuccess) {
        this.addLog('success', `File uploaded to S3 with key: ${uploadKey}`);
        this.addLog('success', `File uploaded to S3 with object: ${uploadSuccess}`);
        return uploadKey;
      } else {
        this.addLog('error', 'Failed to upload file to S3');
        return null;
      }
    } catch (error) {
      this.addLog('error', `S3 upload error: ${error}`);
      return null;
    }
  }

  private async startMigrationProcess(uploadKey: string, _fileName: string, options: MigrationOptions): Promise<void> {
    try {
      const migrationRequest = new MigrateUsersRequest({
        csvS3Url: uploadKey,
        isRevoke: options.revokeOldCampaigns,
        isCreditPoint: options.enableCreditPoints,
        isDebitPoint: options.enableDebitPoints,
        isAddCard: options.enableAddCard,
        isAddTags: options.enableAddTags,
        customTags: options.enableAddTags ? options.customTags : []
      });

      this.addLog('info', 'Calling migration API...');
      this.addLog('info', `Migration request: S3 key=${uploadKey}, Advertiser=${this.advertiserId}`);

      if (options.enableAddCard) {
        this.addLog('info', 'Add Card option enabled');
      }
      if (options.enableAddTags) {
        const tagsDisplay = options.customTags?.length
          ? options.customTags.map((pair: { [key: string]: string }) => {
              const key = Object.keys(pair)[0];
              return `${key}:${pair[key]}`;
            }).join(', ')
          : 'none';
        this.addLog('info', `Add Tags option enabled with custom tags: ${tagsDisplay}`);
      }

      const result: MigrationResult | ActionResultWrapper | string | null = await lastValueFrom(this.campaignClient.migrateUsersFromCsv(migrationRequest));

      if (result) {
        this.addLog('success', 'Migration API call completed successfully');
        this.addLog('info', 'Migration process has been initiated on the server');

        const migrationData = this.extractMigrationData(result);

        if (migrationData.migrationId) {
          this.addLog('info', `Migration ID: ${migrationData.migrationId}`);
          if (migrationData.status) {
            this.addLog('info', `Initial status: ${migrationData.status}`);
          }
          if (migrationData.message) {
            this.addLog('success', `Server message: ${migrationData.message}`);
          }
          this.startMigrationMonitoring(migrationData.migrationId);
        } else {
          this.addLog('warning', 'No migration ID found in result, using fallback monitoring');
          this.addLog('info', `Result structure: ${JSON.stringify(migrationData.rawData, null, 2)}`);
          this.startMigrationMonitoring();
        }
      } else {
        throw new Error('Migration API returned null result');
      }

    } catch (error) {
      this.addLog('error', `Migration API error: ${error}`);
      this.isMigrationRunning = false;
      throw error;
    }
  }

  private async getMigrationProgressWithRetry(migrationId: string, maxRetries: number = 3): Promise<any> {
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        this.addLog('info', `Fetching migration progress (attempt ${attempt}/${maxRetries})...`);

        const progressData = await lastValueFrom(
          this.campaignClient.getMigrationProgress(migrationId)
        );

        if (progressData) {
          return progressData;
        } else {
          throw new Error('No progress data received from API');
        }
      } catch (error) {
        const isLastAttempt = attempt === maxRetries;
        const errorMessage = error instanceof Error ? error.message : String(error);

        if (isLastAttempt) {
          this.addLog('error', `Failed to get migration progress after ${maxRetries} attempts. Final error: ${errorMessage}`);
          throw error;
        } else {
          this.addLog('warning', `Attempt ${attempt} failed: ${errorMessage}. Retrying in ${attempt * 1000}ms...`);

          // Exponential backoff: wait longer between retries
          await new Promise(resolve => setTimeout(resolve, attempt * 1000));
        }
      }
    }

    // This should never be reached, but included for completeness
    throw new Error(`Failed to get migration progress after ${maxRetries} attempts`);
  }

  private async startMigrationMonitoring(migrationId?: string) {
    this.addLog('info', 'Starting migration progress monitoring...');

    if (migrationId) {
      this.addLog('info', `Monitoring migration progress for ID: ${migrationId}`);
      this.addLog('info', 'Refreshing progress every 3 seconds until migration is complete');
      this.addLog('info', '----------------------------------------------------------------------');

      this.monitoringSubscription = interval(3000).pipe(
        takeUntil(this.destroy$)
      ).subscribe(async () => {
        try {
          this.addLog('info', 'Initial delay a few seconds before first progress check');
          setTimeout(() => {}, 5000); // Initial delay before first check
          this.addLog('info', 'Starting monitoring progress checks...');

          await delay(2000); // Small delay to avoid immediate API call
          const progressData = await this.getMigrationProgressWithRetry(migrationId);

          if (progressData) {
            this.updateProgressFromApi(progressData);

            const isStillRunning = this.isMigrationStillRunning(progressData);

            if (!isStillRunning) {
              this.addLog('info', `Migration status changed to: ${progressData.status || 'unknown'}`);
              this.addLog('info', 'Migration is no longer running - stopping progress monitoring');
              if (this.monitoringSubscription) {
                this.monitoringSubscription.unsubscribe();
                this.monitoringSubscription = null;
              }

              if (this.isMigrationComplete(progressData)) {
                this.completeMigration();
              } else {
                this.isMigrationRunning = false;
                this.addLog('error', 'Migration stopped without completing successfully');
              }
            } else {
              this.addLog('info', `Migration in progress: ${progressData.status || 'running'}`);
            }
          } else {
            this.addLog('error', 'No progress data received from API');
          }

        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : String(error);
          this.addLog('error', `Error monitoring migration after retries: ${errorMessage}`);
          this.addLog('error', 'Migration monitoring failed after all retry attempts. Please check the logs for details.');
          if (this.monitoringSubscription) {
            this.monitoringSubscription.unsubscribe();
            this.monitoringSubscription = null;
          }
          this.isMigrationRunning = false;
        }
      });
    } else {
      this.addLog('error', 'No migration ID available, cannot monitor progress');
      this.isMigrationRunning = false;
    }
  }

  private updateProgressFromApi(progressData: MigrationProgressResponse): void {
    try {
      if (progressData.totalUsers !== undefined) {
        this.migrationProgress.total = progressData.totalUsers;
      }
      if (progressData.processedUsers !== undefined) {
        this.migrationProgress.processed = progressData.processedUsers;
      }
      if (progressData.successfulUsers !== undefined) {
        this.migrationProgress.successful = progressData.successfulUsers;
      }
      if (progressData.failedUsers !== undefined) {
        this.migrationProgress.failed = progressData.failedUsers;
      }

      this.migrationProgress.skipped = Math.max(0,
        this.migrationProgress.processed - this.migrationProgress.successful - this.migrationProgress.failed
      );

      if (progressData.progressPercentage !== undefined) {
        this.migrationProgress.percentage = progressData.progressPercentage;
      } else if (this.migrationProgress.total > 0) {
        this.migrationProgress.percentage = Math.round(
          (this.migrationProgress.processed / this.migrationProgress.total) * 100
        );
      }

      if (progressData.message) {
        this.addLog('info', `Status: ${progressData.message}`);
      }

      this.addLog('info',
        `Progress update: ${this.migrationProgress.processed}/${this.migrationProgress.total} ` +
        `(${this.migrationProgress.percentage}%) - ` +
        `Success: ${this.migrationProgress.successful}, ` +
        `Failed: ${this.migrationProgress.failed}, ` +
        `Skipped: ${this.migrationProgress.skipped}`
      );

      if (progressData.estimatedTimeRemaining) {
        this.addLog('info', `Estimated time remaining: ${progressData.estimatedTimeRemaining}`);
      }

      const errorReportUrl = (progressData as any).errorReportS3Url ||
                            (progressData as any).ErrorReportS3Url;
      if (errorReportUrl && errorReportUrl !== this.errorReportS3Url) {
        this.errorReportS3Url = errorReportUrl;
        this.addLog('info', 'Error report available for download');
      }

    } catch (error) {
      this.addLog('error', `Error updating progress from API: ${error}`);
    }
  }

  private isMigrationComplete(progressData: MigrationProgressResponse): boolean {
    if (progressData.status) {
      const status = progressData.status.toLowerCase();
      return status === 'completed' || status === 'finished' || status === 'done' || status === 'success';
    }

    if (progressData.isCompleted !== undefined) {
      return progressData.isCompleted;
    }

    if (progressData.totalUsers && progressData.processedUsers) {
      return progressData.processedUsers >= progressData.totalUsers;
    }

    return this.migrationProgress.total > 0 && this.migrationProgress.processed >= this.migrationProgress.total;
  }

  private isMigrationStillRunning(progressData: MigrationProgressResponse): boolean {
    if (progressData.status) {
      const status = progressData.status.toLowerCase();
      return status === 'running' || status === 'in-progress' || status === 'processing' || status === 'active';
    }

    if (progressData.isCompleted !== undefined) {
      return !progressData.isCompleted;
    }

    if (progressData.totalUsers && progressData.processedUsers) {
      return progressData.processedUsers < progressData.totalUsers;
    }

    return this.isMigrationRunning && !this.migrationCompleted;
  }

  private completeMigration(): void {
    this.isMigrationRunning = false;
    this.migrationCompleted = true;
    this.addLog('success', 'Migration process completed');

    const message = `Migration completed: ${this.migrationProgress.successful} successful, ${this.migrationProgress.failed} failed, ${this.migrationProgress.skipped} skipped`;
    this.snackBar.open(message, 'Close', { duration: 5000 });
  }

  resetForm(clearLogs: boolean = false): void {
    this.migrationForm.reset({
      revokeOldCampaigns: false,
      enableDebitPoints: false,
      enableCreditPoints: false,
      enableAddCard: true,
      enableAddTags: false,
      customTags: []
    });
    this.selectedFiles = [];
    this.isMigrationRunning = false;
    this.resetProgress();

    if (clearLogs) {
      this.migrationLogs = [];
      this.migrationEverStarted = false;
      this.migrationCompleted = false;
      this.errorReportS3Url = null;
      this.addLog('info', 'Form reset - all logs and history cleared');
    } else {
      this.addLog('info', 'Form reset - logs preserved for review');
    }
  }

  private resetProgress(): void {
    this.migrationProgress = {
      total: 0,
      processed: 0,
      successful: 0,
      failed: 0,
      skipped: 0,
      percentage: 0
    };
  }

  getProgressBarColor(): 'primary' | 'accent' | 'warn' {
    if (this.migrationProgress.failed > 0) {
      return 'warn';
    } else if (this.migrationProgress.percentage === 100) {
      return 'accent';
    }
    return 'primary';
  }

  downloadResults(): void {
    const results = {
      migration_summary: {
        total_campaigns: this.migrationProgress.total,
        successful: this.migrationProgress.successful,
        failed: this.migrationProgress.failed,
        skipped: this.migrationProgress.skipped,
        completion_percentage: this.migrationProgress.percentage
      },
      migration_logs: this.migrationLogs.map(log => ({
        timestamp: log.timestamp.toISOString(),
        level: log.level,
        message: log.message
      }))
    };

    const blob = new Blob([JSON.stringify(results, null, 2)], { type: 'application/json' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `migration_report_${new Date().toISOString().split('T')[0]}.json`;
    link.click();
    window.URL.revokeObjectURL(url);

    this.addLog('info', 'Migration report downloaded');
  }

  downloadErrors(): void {
    if (this.errorReportS3Url) {
      const link = document.createElement('a');
      link.href = this.errorReportS3Url;
      link.target = '_blank';
      link.download = `migration_errors_${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
      this.addLog('info', 'Error report downloaded from server');
    } else {
      const errorLogs = this.migrationLogs.filter(log => log.level === 'error');
      const csvContent = [
        'timestamp,message',
        ...errorLogs.map(log => `"${log.timestamp.toISOString()}","${log.message}"`)
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `migration_errors_${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
      window.URL.revokeObjectURL(url);

      this.addLog('info', 'Error report downloaded from logs');
    }
  }

  async addLog(level: 'info' | 'success' | 'warning' | 'error', message: string): Promise<void> {
    this.migrationLogs.push({
      timestamp: new Date(),
      level,
      message
    });

    // Keep only last 100 logs
    if (this.migrationLogs.length > 100) {
      this.migrationLogs = this.migrationLogs.slice(-100);
    }

    this.shouldScrollToBottom = true;
  }

  isMonitoringActive(): boolean {
    return this.isMigrationRunning && this.monitoringSubscription && !this.monitoringSubscription.closed;
  }

  clearLogs(): void {
    this.migrationLogs = [];
    this.migrationCompleted = false;
    this.migrationEverStarted = false;
    this.errorReportS3Url = null;
  }

  stopMigrationMonitoring(): void {
    if (this.monitoringSubscription) {
      this.monitoringSubscription.unsubscribe();
      this.monitoringSubscription = null;
    }
    this.isMigrationRunning = false;
    this.addLog('warning', 'Migration monitoring stopped manually');
  }

  trackByLogEntry(index: number, log: MigrationLog): string {
    return `${log.timestamp.getTime()}-${log.level}-${index}`;
  }

  private extractMigrationData(result: any): {
    migrationId?: string;
    status?: string;
    message?: string;
    success?: boolean;
    errorReportS3Url?: string;
    rawData?: any;
  } {
    let migrationId: string | undefined;
    let status: string | undefined;
    let message: string | undefined;
    let success: boolean | undefined;
    let errorReportS3Url: string | undefined;
    let extractedData: any = result;

    try {
      if (typeof result === 'string') {
        return {
          migrationId: result,
          success: true,
          rawData: result
        };
      }

      if (!result || typeof result !== 'object') {
        return { rawData: result };
      }

      if ('toJSON' in result && typeof result.toJSON === 'function') {
        try {
          extractedData = result.toJSON();
        } catch (jsonError) {
          console.warn('Failed to call toJSON():', jsonError);
          extractedData = result;
        }
      }

      const dataProperties = ['data', 'result', 'value', 'response'];
      for (const prop of dataProperties) {
        if (extractedData && typeof extractedData === 'object' && prop in extractedData) {
          const nestedData = extractedData[prop];
          if (nestedData && typeof nestedData === 'object') {
            extractedData = nestedData;
            break;
          }
        }
      }

      const idProperties = ['migrationId', 'id', 'MigrationId', 'ID', 'migration_id', 'taskId', 'jobId'];
      for (const prop of idProperties) {
        if (extractedData && extractedData[prop]) {
          migrationId = String(extractedData[prop]);
          break;
        }
      }

      if (extractedData) {
        status = extractedData.status || extractedData.Status || extractedData.state;
        message = extractedData.message || extractedData.Message || extractedData.description;
        success = extractedData.success !== undefined ? extractedData.success :
                 extractedData.Success !== undefined ? extractedData.Success :
                 (migrationId ? true : undefined);

        errorReportS3Url = extractedData.errorReportS3Url ||
                          extractedData.ErrorReportS3Url ||
                          extractedData.error_report_s3_url ||
                          extractedData.errorReportUrl ||
                          extractedData.ErrorReportUrl;
      }

      if (!migrationId && result && typeof result === 'object') {
        for (const prop of idProperties) {
          if (result[prop]) {
            migrationId = String(result[prop]);
            break;
          }
        }
      }

    } catch (error) {
      console.error('Error extracting migration data:', error);
    }

    return {
      migrationId,
      status,
      message,
      success,
      errorReportS3Url,
      rawData: extractedData
    };
  }
}
